import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDateTime } from '@/lib/format'

export function Kyc() {
  const { t, i18n } = useTranslation('admin')
  const [page, setPage] = useState(1)
  const query = useQuery({ queryKey: ['admin-kyc', page], queryFn: () => adminApi.listKyc(page) })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('kyc.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('kyc.subtitle')}</p>
      </header>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('kyc.col_user')}</th>
                <th className="px-4 py-2 font-medium">{t('kyc.col_tier')}</th>
                <th className="px-4 py-2 font-medium">{t('kyc.col_status')}</th>
                <th className="px-4 py-2 font-medium">{t('kyc.col_created')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((k) => (
                <tr key={k.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-mono">{k.user.email}</td>
                  <td className="px-4 py-2 uppercase">{k.tier}</td>
                  <td className="px-4 py-2">{k.status}</td>
                  <td className="px-4 py-2 text-text-muted">
                    {formatDateTime(k.created_at, i18n.language)}
                  </td>
                </tr>
              ))}
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-text-muted">
                    {t('kyc.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
